<template>
  <div class="wrapper">
    <div class="detail">
      <h4>Check your order details:</h4>

      <div class="campaign-create-sub-title order-title">Your order:</div>

      <table class="order-table">
        <tr>
          <th>Number of creators:</th>
          <td>{{ props.campaign.number_of_creators }} {{ props.campaign.number_of_creators === 1 ? 'creator' : 'creators' }}</td>
        </tr>
        <tr>
          <th>Charge per creator:</th>
          <td>${{ props.price }}</td>
        </tr>
      </table>

      <hr>

      <div class="total">
        <div class="total-title">Due Today:</div>
        <div class="total-price">${{ totalPrice }}</div>
      </div>

      <Loader size="small" v-if="isCardsLoading" />

      <div v-else class="checkout-payment">
        <div class="checkout-payment-title">Payment method</div>
        <a href="#">How I will be charged?</a>

        <div v-if="defaultCard" class="checkout-payment-card">
          {{ defaultCard.type }} - **** **** **** {{ defaultCard.last4 }}
        </div>

        <div class="checkout-payment-card-form"  v-else>
          <StripeCard ref="stripeCard" />
        </div>

      </div>
    </div>
    <div class="timeline-column">
      <CampaignTimeline :data="timelines" />
    </div>

    <PaymentSuccessfulModal :visible="successfulModalVisible"
                            :textMessage="'Your payment was successful <br /> and your campaign is now active.'"
                            :successButtonLabel="'My Campaigns'"
                            @successAction="goToDashboard"
    />
    <ErrorModal v-model:visible="errorModalVisible">
      <template #title>Uh oh!</template>
      <template #buttons>
        <Button label="Try again" class="p-button-md" @click="submit" :loading="busy" />
        <Button label="Back to Campaign" class="p-button-md back-to-campaign-button p-button-secondary" @click="closeErrorModal()" />
      </template>
      {{ errorMessage }}
    </ErrorModal>

  </div>

  <Footer>
    <Button label="Previous step" class="p-button-text p-button-secondary" icon="pi pi-angle-left" @click.prevent="emit('onBack')" />
    <div class="footer-price">Total: ${{ totalPrice }}</div>
    <Button label="Create Campaign" class="checkout-button p-button-md" @click.prevent="submit" :loading="busy" />
  </Footer>
</template>

<script setup>
import Footer from "./Footer";
import {computed, ref} from "vue";
import {useCampaign} from "../../../hooks/useCampaign";
import CampaignTimeline from "../../CampaignTimeline";
import PaymentSuccessfulModal from "../CampaignPaymentSuccessfulModal";
import ErrorModal from "./../../ErrorModal";
import {useForm} from "../../../hooks/useForm";
import {useAdTemplate} from "../../../hooks/useAdTemplate";
import StripeCard from "../../StripeCard";
import Loader from "../../Loader";
import {usePaymentCard} from "../../../hooks/usePaymentCard";
import {getCards} from "../../../queries/paymentCardQueries";
import {useRouter} from "vue-router";

const {create, update, prepareToSave, checkout, calculateTimeline} = useCampaign();
const {getTemplates} = useAdTemplate();
const {create: createPaymentCard} = usePaymentCard();
const router = useRouter();

const emit = defineEmits(['onBack', 'checkout']);
const props = defineProps({
  campaign: {type: Object, required: true},
  price: {type: Number, required: true},
});
const template = (await getTemplates()).data.filter((item)=> item.id === props.campaign.ad_template_id);
const id = ref(props.campaign.id);
const timelines = ref((await calculateTimeline({skip_products: props.campaign.products.length === 0})).data);
const stripeCard = ref();

const {data: cardsData, isLoading: isCardsLoading} = getCards({cacheTime: 0});
const cards = computed(() => cardsData.value?.data || []);
const defaultCard = computed(() => cards.value.find(card => card.is_default))

const detailsModalVisible = ref(false);
const successfulModalVisible = ref(false);
const errorModalVisible = ref(false);

const {getError, busy, submit, errorMessage} = useForm(async () => {
  try {
    if (!defaultCard.value) {
      const setupIntent = await stripeCard.value.submit();
      await createPaymentCard({pm_id: setupIntent.payment_method});
    }

    const data = prepareToSave(props.campaign);
    const newCampaignData = await (id.value ? (await update(id.value, data)) : (await create(data))).data;
    const checkOutData = await checkout(newCampaignData.id);
    window.dataLayer.push(checkOutData);
    successfulModalVisible.value = true;
    errorModalVisible.value = false;
  } catch (e) {
    successfulModalVisible.value = false;
    errorModalVisible.value = true;

    throw e;
  }
})

const totalSeconds = computed(() => props.campaign.scripts.reduce((prev, current) => prev + current.duration, 0));
const totalPrice = computed(() => props.campaign.number_of_creators * props.price);
const closeErrorModal = () => errorModalVisible.value = false;

const campaignDetails = computed(() => {
  return {
    ...props.campaign,
    demographics: [...props.campaign.age, ...props.campaign.gender],
    timeline: timelines
  }
})
const showDetails = () => detailsModalVisible.value = true;

const goToDashboard = () => router.push({name: 'dashboard'});
</script>

<style lang="scss" scoped>
@import "../../../assets/sass/_theme_variables.scss";

.wrapper {
  flex: 1;
  display: flex;
  width: toRem(940);
  margin: 0 auto;
}

.detail {
  flex: 1 1 auto;
  padding-right: toRem(32);
  border-right: 1px solid $shade300;
}

.timeline-column {
  flex: 0 0 auto;
  width: toRem(370);
  padding-left: toRem(32);
}

.campaign-create-sub-title {
  margin: toRem(30) 0 toRem(12);
  font-size: 18px;
  font-weight: bold;
}

h4 {
  font-weight: bold;
}

.order-title {
  margin-top: toRem(46);
}

.order-table {
  width: 100%;
  font-size: toRem(18);

  th, td {
    padding: toRem(9) 0;
  }

  th {
    font-weight: normal;
    text-align: left;
  }

  td {
    font-weight: 600;
    text-align: right;
  }
}

.script-description {
  display: flex;
  margin-top: toRem(20);
  margin-bottom: toRem(24);
  padding: toRem(10) toRem(3) toRem(10) toRem(15);
  border: 1px solid $shade300;
  border-radius: 8px;
  font-weight: 600;
  justify-content: space-between;
  align-items: center;
}

.scripts-title {
  margin-top: toRem(24);
}

.total {
  display: flex;
  margin-top: toRem(24);
  font-weight: bold;
  font-size: 18px;
}

.total-price {
  margin-left: auto;
}

.message {
  margin-top: toRem(32);
  border: none;

  ::v-deep(.p-message-wrapper) {
    align-items: start;

    .p-message-icon {
      margin-top: toRem(3);
    }
  }
}

.footer-price {
  display: flex;
  align-items: center;
  margin-left: auto;
  margin-right: toRem(16);
  padding-right: toRem(16);
  font-weight: 600;
  font-size: toRem(18);
  border-right: 1px solid $shade300;
}

.checkout-button {
  font-size: toRem(16);
  padding-left: toRem(20);
  padding-right: toRem(20);
}

.component-footer {
  ::v-deep(.footer-content) {
    max-width: toRem(940);
  }
}

.back-to-campaign-button {
  margin-top: toRem(12);
}

.checkout-payment {
  display: flex;
  flex-wrap: wrap;
  margin-top: toRem(45);
  justify-content: space-between;
}

.checkout-payment-title {
  font-weight: 700;
  font-size: toRem(24);
}

.checkout-payment-card, .checkout-payment-card-form {
  margin-top: toRem(30);
  width: 100%;
}
</style>
